import { todayPT } from "@homebound/activesupport";
import { Css, Palette, useTestIds } from "@homebound/beam";

import { useMemo } from "react";
import { LineChart, type ChartSeries } from "src/components/charts/LineChart";
import { getNumberTicks } from "src/components/charts/utils";
import { DirectMarginTab_ChartDataFragment } from "src/routes/projects/budget/components/reports/DirectMarginTab.generated";
import { zeroTo } from "src/utils";
import { Temporal } from "temporal-polyfill";
import { BudgetMetricsViewType } from "../utils";

type DirectMarginOverTimeWidgetProps = { viewType: BudgetMetricsViewType; data: DirectMarginTab_ChartDataFragment };

export function DirectMarginOverTimeWidget({ data, viewType }: DirectMarginOverTimeWidgetProps) {
  const tid = useTestIds({});
  const chartData = useMemo(() => mapToLineChartData(data, viewType), [data, viewType]);
  // Get axis ticks from data
  const { yTicks, xTicks } = useMemo(
    () =>
      getChartTicks(
        chartData.flatMap((p) => p.data).filter((d) => d.y !== null),
        viewType,
      ),
    [chartData, viewType],
  );
  // Determine if the chart data spans multiple years
  const isMultiYearData = useMemo(() => {
    const points = chartData.flatMap((p) => p.data);
    return points.length > 2 && points.first!.x.getFullYear() !== points.last!.x.getFullYear();
  }, [chartData]);

  return (
    <div css={Css.p3.bgWhite.br8.hPx(311).ba.bcGray300.$}>
      <div css={Css.gray800.md.mb3.$}>Direct Margin Over Time</div>
      <div css={Css.h("80%").$} {...tid.LineChart}>
        <LineChart
          dataset={chartData.isEmpty ? generateEmptyDataset(0, 30, viewType) : chartData}
          colors={[chartData.isEmpty ? Palette.Transparent : Palette.Blue400]}
          loadingColor={[Palette.Transparent]}
          disabled={chartData.isEmpty}
          emptyStateTooltipText={chartData.isEmpty ? "There are no projects that have started yet." : undefined}
          tooltipValueFormat={(points) => `${(points.first!.data.y as number).toFixed(2)}%`}
          margin={{ top: 5, right: 20, bottom: 30, left: 55 }}
          axisLeft={{
            tickValues: yTicks,
            tickPadding: 20,
            tickSize: 0,
            format: (value: number) => `${+value.toFixed(1)}%`,
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 15,
            tickValues: xTicks,
            format:
              viewType === BudgetMetricsViewType.MTD || viewType === BudgetMetricsViewType.QTD
                ? "%b %d"
                : isMultiYearData
                  ? "%b '%y"
                  : "%b",
          }}
          enableSlices="x"
          yScale={{ type: "linear", min: "auto", max: "auto" }}
          gridYValues={yTicks}
          xScale={{ type: "time", format: "%Y-%m-%d", precision: "day" }}
          xFormat="time:%b %Y"
          theme={{ axis: { ticks: { text: { fontSize: 12, fill: Palette.Gray700 } } } }}
        />
      </div>
    </div>
  );
}

function mapToLineChartData({ data }: DirectMarginTab_ChartDataFragment, viewType: BudgetMetricsViewType) {
  const points = data.map(({ date, directMarginInBasisPoints }) => ({
    x: date.toPT().toLegacyDate(),
    y: (directMarginInBasisPoints ?? 0) / 100,
  }));
  // Add a null datapoint at the end of the time range to ensure the chart uses full width
  const today = todayPT();
  const endDate: Temporal.PlainDate = (() => {
    switch (viewType) {
      case BudgetMetricsViewType.MTD:
        return endOfMonthPlainDate(today);
      case BudgetMetricsViewType.QTD:
        return endOfQuarterPlainDate(today);
      case BudgetMetricsViewType.YTD:
        return endOfYearPlainDate(today);
      default:
        return today;
    }
  })();
  return points.nonEmpty
    ? [{ id: "Direct Margin Over Time", data: [...points, { x: endDate.toPT().toLegacyDate(), y: null }] }]
    : [];
}

function getChartTicks(
  data: { x: Date; y: number }[],
  viewType: BudgetMetricsViewType,
): { xTicks: Date[] | undefined; yTicks: number[] } {
  // Get the number of ticks based on the view type
  return data.isEmpty
    ? { xTicks: undefined, yTicks: [0, 7.5, 15, 22.5, 30] }
    : {
        xTicks: getFixedDateTicks(
          data.map((d) => d.x),
          viewType,
        ),
        yTicks: getNumberTicks(
          data.map((d) => d.y),
          5,
        ),
      };
}

function getFixedDateTicks(data: Date[], view: BudgetMetricsViewType): Date[] {
  const today = todayPT();
  const yearToDateTicks = zeroTo(12).map((i) => toLegacyDate(startOfYearPlainDate(today).add({ months: i })));

  function generateDateRange(
    start: Temporal.PlainDate,
    end: Temporal.PlainDate,
    dateFn: (date: Temporal.PlainDate) => Temporal.PlainDate,
  ): Date[] {
    const ticks: Date[] = [];
    let date = start;
    while (date.isBefore(end) || date.equals(end)) {
      ticks.push(toLegacyDate(date));
      date = dateFn(date);
    }
    return ticks;
  }

  // Return ticks based on the view type
  switch (view) {
    case BudgetMetricsViewType.YTD: {
      // Tick at the start of each month from Jan to Dec
      return yearToDateTicks;
    }

    case BudgetMetricsViewType.QTD: {
      const startDate = startOfQuarterPlainDate(today);
      const endDate = endOfQuarterPlainDate(today);
      // Ticks for each Monday in the quarter
      const ticks: Date[] = [];
      let date = startDate;
      while (date.isBefore(endDate) || date.equals(endDate)) {
        if (date.dayOfWeek === 1) {
          ticks.push(toLegacyDate(date));
        }
        date = date.add({ days: 1 });
      }
      return ticks;
    }

    case BudgetMetricsViewType.MTD: {
      // Ticks every 3 days of current month
      return generateDateRange(startOfMonthPlainDate(today), endOfMonthPlainDate(today), (date) =>
        date.add({ days: 3 }),
      );
    }

    default: {
      const startDate = toPlainDate(data.min());
      const endDate = toPlainDate(data.max());
      // Treat like YTD until we get more than a year of data
      if (startDate.year === endDate.year) {
        return yearToDateTicks;
      }
      // Return monthly ticks
      return generateDateRange(startDate, endDate, (date) => date.add({ months: 1 }));
    }
  }
}

function generateEmptyDataset(yMin: number, yMax: number, viewType: BudgetMetricsViewType): ChartSeries[] {
  const today = todayPT();
  const dateRange: Record<BudgetMetricsViewType, [Temporal.PlainDate, Temporal.PlainDate]> = {
    [BudgetMetricsViewType.MTD]: [startOfMonthPlainDate(today), endOfMonthPlainDate(today)],
    [BudgetMetricsViewType.QTD]: [startOfQuarterPlainDate(today), endOfQuarterPlainDate(today).add({ days: 1 })],
    [BudgetMetricsViewType.YTD]: [
      endOfMonthPlainDate(startOfYearPlainDate(today)),
      endOfYearPlainDate(today).add({ days: 5 }),
    ],
    [BudgetMetricsViewType.ALL_TIME]: [today.subtract({ years: 1 }), today],
  };

  const [start, end] = dateRange[viewType];
  return [
    { id: "empty_dataset", data: [start, end].map((x, i) => ({ x: toLegacyDate(x), y: i === 0 ? yMin : yMax })) },
  ];
}

function startOfMonthPlainDate(date: Temporal.PlainDate) {
  return date.with({ day: 1 });
}

function startOfQuarterPlainDate(date: Temporal.PlainDate) {
  const startOfQuarterMonth = Math.floor((date.month - 1) / 3) * 3 + 1;
  return date.with({ month: startOfQuarterMonth, day: 1 });
}

function startOfYearPlainDate(date: Temporal.PlainDate) {
  return date.with({ month: 1, day: 1 });
}

function toLegacyDate(date: Temporal.PlainDate) {
  return date.toPT().toLegacyDate();
}

function toPlainDate(date: Date) {
  return Temporal.PlainDate.from({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });
}

function endOfMonthPlainDate(date: Temporal.PlainDate) {
  return date.add({ months: 1 }).with({ day: 1 }).subtract({ days: 1 });
}

function endOfQuarterPlainDate(date: Temporal.PlainDate) {
  const startOfQuarterMonth = Math.floor((date.month - 1) / 3) * 3 + 1;
  return date.with({ month: startOfQuarterMonth, day: 1 }).add({ months: 3 }).subtract({ days: 1 });
}

function endOfYearPlainDate(date: Temporal.PlainDate) {
  return date.with({ month: 12, day: 31 });
}
